import React from 'react';

import { Box, createStyles, makeStyles, Theme, useTheme } from '@material-ui/core';
import clsx from 'clsx';

const useStyles = makeStyles<
  Theme,
  {
    activeColor?: string;
    inactiveColor?: string;
    height?: number;
    segmentCount: number;
    activeIndex: number;
  }
>((theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      display: 'flex',
      borderRadius: '8px',
      cursor: 'pointer',
      boxShadow: '2px 4px 12px rgba(20, 20, 43, 0.04)',
      overflow: 'hidden',
    },
    background: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: props => props.inactiveColor || '#E0E0E0',
    },
    activeBlock: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: props => `${100 / props.segmentCount}%`,
      backgroundColor: props => props.activeColor || '#673AB7',
      borderRadius: '8px',
      transition: theme.transitions.create(['transform'], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
      transform: props => `translateX(${props.activeIndex * 100}%)`,
    },
    segmentsContainer: {
      position: 'relative',
      display: 'flex',
      width: '100%',
      zIndex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    segment: {
      flex: '1 1 0%',
      minWidth: 0,
      padding: '16px 20px',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: props => props.height || 80,
      textAlign: 'center',
      transition: theme.transitions.create(['color'], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
    segmentContent: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      minWidth: 0,
      textAlign: 'center',
      '& > *': {
        textAlign: 'center',
      },
    },
    activeText: {
      color: '#FFFFFF',
    },
    inactiveText: {
      color: '#A0A0A0',
    },
  })
);

export interface AppSwitchOption {
  content: React.ReactNode;
  /** Used in multi-segment mode (when `options` is provided) to identify the segment. */
  value?: string;
}

export interface AppSwitchProps {
  /** Legacy two-segment API. */
  leftOption?: AppSwitchOption;
  rightOption?: AppSwitchOption;
  checked?: boolean;
  /** Multi-segment API — provide 2+ options and a controlled `value`. */
  options?: AppSwitchOption[];
  value?: string;
  /** Receives a boolean in the legacy two-segment mode, or the option value in multi-segment mode. */
  onChange: (value: any) => void;
  activeColor?: string;
  inactiveColor?: string;
  height?: number;
  className?: string;
}

export const AppSwitch: React.FC<AppSwitchProps> = ({
  leftOption,
  rightOption,
  checked,
  options,
  value,
  onChange,
  activeColor,
  inactiveColor,
  height = 40,
  className,
}) => {
  const theme = useTheme();
  inactiveColor = inactiveColor || theme.palette.background.paper;
  activeColor = activeColor || theme.palette.primary.main;

  const isMulti = Array.isArray(options) && options.length > 0;
  const segments: AppSwitchOption[] = isMulti
    ? (options as AppSwitchOption[])
    : [leftOption ?? { content: null }, rightOption ?? { content: null }];

  const activeIndex = isMulti
    ? Math.max(
        0,
        segments.findIndex(segment => segment.value === value)
      )
    : checked
    ? 1
    : 0;

  const classes = useStyles({
    activeColor,
    inactiveColor,
    height,
    segmentCount: segments.length,
    activeIndex,
  });

  const handleSegmentClick = (index: number) => {
    if (isMulti) {
      onChange(segments[index].value);
    } else {
      onChange(index === 1);
    }
  };

  return (
    <Box className={clsx(classes.root, className)} height={height}>
      <Box className={classes.background} />
      <Box className={classes.activeBlock} />
      <Box className={classes.segmentsContainer}>
        {segments.map((segment, index) => (
          <Box
            key={segment.value ?? index}
            className={clsx(classes.segment, {
              [classes.activeText]: index === activeIndex,
              [classes.inactiveText]: index !== activeIndex,
            })}
            onClick={() => handleSegmentClick(index)}
          >
            <Box className={classes.segmentContent}>{segment.content}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
